/**
 * Embed Widget Guide
 * Instructions for embedding the public booking widget
 */

import {
  Code,
  Globe,
  Palette,
  Link2,
  Copy,
  CheckCircle2,
  Settings,
  ExternalLink,
} from 'lucide-react';
import { useState } from 'react';
import { pageStyles, cardStyles } from '../../lib/styles';

export function EmbedWidget() {
  const [copied, setCopied] = useState<string | null>(null);

  // Derive API URL from current origin (crm.* -> api.*)
  const apiUrl = (() => {
    const origin = window.location.origin;
    if (origin.includes('crm.')) {
      return origin.replace('crm.', 'api.');
    }
    if (origin.includes('localhost')) {
      return origin.replace(':5173', ':8787');
    }
    return import.meta.env.VITE_API_URL || 'https://api.lealchapaypintura.com';
  })();
  const widgetUrl = `${apiUrl}/embed`;

  const iframeCode = `<iframe
  src="${widgetUrl}"
  width="100%"
  height="700"
  frameborder="0"
  style="border: none; border-radius: 8px;"
  title="Reserva tu cita"
></iframe>`;

  const linkCode = `<a href="${widgetUrl}" target="_blank" rel="noopener noreferrer">
  Reservar Cita Online
</a>`;

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className={pageStyles.container}>
      {/* Introduction */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Widget de Reservas Online</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <p className="text-xs text-gray-600">
            El widget de reservas permite a tus clientes reservar citas
            directamente desde tu pagina web.
          </p>
          <div className="mt-2 px-2 py-1.5 bg-green-50 text-green-700 rounded text-[11px]">
            <strong>Ventaja:</strong> Reduce llamadas telefonicas y permite a
            los clientes reservar 24/7.
          </div>
        </div>
      </div>

      {/* Widget URL */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Link2 className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">URL del Widget</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <p className="text-xs text-gray-500 mb-2">
            URL directa del widget. Comparte con clientes o usa para integrar.
          </p>
          <div className="flex items-center gap-1.5">
            <code className="flex-1 px-2 py-1.5 bg-gray-50 rounded text-[11px] break-all font-mono">
              {widgetUrl}
            </code>
            <button
              onClick={() => copyToClipboard(widgetUrl, 'url')}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title="Copiar URL"
            >
              {copied === 'url' ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          <div className="mt-2">
            <a
              href={widgetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
            >
              <ExternalLink className="h-3 w-3" />
              Abrir widget
            </a>
          </div>
        </div>
      </div>

      {/* Iframe Integration */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Code className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Integracion con Iframe</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <p className="text-xs text-gray-500 mb-2">
            Copia el codigo y pegalo en tu pagina web.
          </p>
          <div className="relative">
            <pre className="p-2 bg-gray-900 text-gray-100 rounded overflow-x-auto text-[10px] leading-relaxed">
              <code>{iframeCode}</code>
            </pre>
            <button
              onClick={() => copyToClipboard(iframeCode, 'iframe')}
              className="absolute top-1.5 right-1.5 p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
              title="Copiar codigo"
            >
              {copied === 'iframe' ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          <div className="mt-2 space-y-1 text-[11px] text-gray-500">
            <p><strong>width:</strong> 100% o valor fijo (400px)</p>
            <p><strong>height:</strong> Recomendado 700px</p>
          </div>
        </div>
      </div>

      {/* Link Integration */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Integracion con Enlace</span>
        </div>
        <div className={cardStyles.content}>
          <p className="text-xs text-gray-500 mb-2">
            Abre el widget en una nueva pestana.
          </p>
          <div className="relative">
            <pre className="p-2 bg-gray-900 text-gray-100 rounded overflow-x-auto text-[10px] leading-relaxed">
              <code>{linkCode}</code>
            </pre>
            <button
              onClick={() => copyToClipboard(linkCode, 'link')}
              className="absolute top-1.5 right-1.5 p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
              title="Copiar codigo"
            >
              {copied === 'link' ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Customization */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Palette className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Personalizacion</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <p className="text-xs text-gray-500 mb-2">
            Anade parametros a la URL para personalizar.
          </p>
          <div className="space-y-1.5">
            {[
              { param: '?primaryColor=hex', desc: 'Color principal (ej: 3B82F6)' },
              { param: '?bgColor=hex', desc: 'Color de fondo (ej: FFFFFF)' },
              { param: '?textColor=hex', desc: 'Color de texto (ej: 111827)' },
              { param: '?radius=number', desc: 'Bordes redondeados en px' },
              { param: '?hideLogo=1', desc: 'Oculta el logo superior' },
              { param: '?logoUrl=url&logoHeight=n', desc: 'Logo personalizado' },
              { param: '?hideHeader=1&showProgress=0', desc: 'Oculta cabecera/progreso' },
              { param: '?variant=flat|card&maxWidth=600px', desc: 'Estilo y ancho' },
              { param: '?serviceType=id', desc: 'Pre-selecciona servicio' },
            ].map(({ param, desc }) => (
              <div key={param} className="flex items-start gap-2 px-2 py-1.5 border border-gray-100 rounded">
                <code className="text-[10px] font-mono text-gray-900 shrink-0">{param}</code>
                <span className="text-[10px] text-gray-500">{desc}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 px-2 py-1.5 bg-blue-50 rounded">
            <p className="text-[10px] text-blue-700">
              <strong>Ejemplo:</strong>{' '}
              <code className="break-all">{widgetUrl}?primaryColor=3B82F6&variant=card</code>
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Requirements */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Settings className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Requisitos</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <p className="text-xs text-gray-500 mb-2">
            Para que el widget funcione correctamente:
          </p>
          <ul className="space-y-1.5">
            {[
              { title: 'Tipos de servicio activos', desc: 'Al menos uno activo' },
              { title: 'Horario de apertura', desc: 'Para mostrar slots' },
              { title: 'Reglas de reserva', desc: 'Antelacion minima/maxima' },
              { title: 'Bahias de trabajo', desc: 'Al menos una activa' },
            ].map(({ title, desc }) => (
              <li key={title} className="flex items-start gap-1.5">
                <CheckCircle2 className="h-3 w-3 text-green-500 mt-0.5 shrink-0" />
                <span className="text-[11px] text-gray-600">
                  <strong className="text-gray-900">{title}:</strong> {desc}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Troubleshooting */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Solucion de Problemas</span>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-2">
            {[
              { title: 'El widget no carga', desc: 'Verifica que tu servidor permite iframes externos.' },
              { title: 'Sin horarios disponibles', desc: 'Revisa horarios de apertura y bahias activas.' },
              { title: 'Sin confirmacion', desc: 'Activa notificaciones de confirmacion.' },
            ].map(({ title, desc }) => (
              <div key={title} className="px-2 py-1.5 border-l-2 border-amber-400 bg-amber-50 rounded-r">
                <p className="text-[11px] font-medium text-amber-900">{title}</p>
                <p className="text-[10px] text-amber-700">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmbedWidget;
